import { useEffect, useCallback } from "react";
import {
  useGlobalSelectionStore,
  TableType,
  CursorMode,
} from "../store/useGlobalSelectionStore";
import { usePropertyStore } from "@/store/usePropertyStore";

/**
 * Custom hook for table components to manage global selection
 */
export const useTableSelection = (tableType: TableType) => {
  const store = useGlobalSelectionStore();

  const isActive = store.activeTable === tableType;
  const activeRowId = isActive ? store.activeRowId : null;

  /**
   * Select a row (clears other table selections automatically)
   */
  const selectRow = useCallback(
    (rowId: string, rowData?: any) => {
      store.setActiveTableRow(tableType, rowId, rowData);
    },
    [tableType, store]
  );

  /**
   * Unselect the current row
   */
  const unselectRow = useCallback(() => {
    if (isActive) {
      store.clearTableSelection();
    }
  }, [isActive, store]);

  /**
   * Check if a specific row is selected
   */
  const isRowSelected = useCallback(
    (rowId: string) => {
      return store.isRowActive(tableType, rowId);
    },
    [tableType, store]
  );

  /**
   * Toggle row selection
   */
  const toggleRow = useCallback(
    (rowId: string, rowData?: any) => {
      if (isRowSelected(rowId)) {
        unselectRow();
      } else {
        selectRow(rowId, rowData);
      }
    },
    [isRowSelected, unselectRow, selectRow]
  );

  return {
    isActive,
    activeRowId,
    activeRowData: store.activeRowData,
    selectRow,
    unselectRow,
    isRowSelected,
    toggleRow,
  };
};

/**
 * Custom hook for cursor mode management
 */
export const useCursorMode = () => {
  const store = useGlobalSelectionStore();

  /**
   * Set cursor mode (clears previous mode automatically)
   */
  const setCursorMode = useCallback(
    (mode: CursorMode, data?: any) => {
      store.setActiveCursorMode(mode, data);

      (window as any).__applyByClickActive = true;
    },
    [store]
  );

  /**
   * Clear cursor mode
   */
  const clearCursorMode = useCallback(() => {
    store.clearCursorMode();

    (window as any).__applyByClickActive = false;
  }, [store]);

  /**
   * Check if a specific cursor mode is active
   */
  const isCursorModeActive = useCallback(
    (mode: CursorMode) => {
      return store.isCursorModeActive(mode);
    },
    [store]
  );

  return {
    activeCursorMode: store.activeCursorMode,
    cursorModeData: store.cursorModeData,
    setCursorMode,
    clearCursorMode,
    isCursorModeActive,
  };
};

export const useCancelAllApplyModes = () => {
  const clearCursorMode = useCursorMode().clearCursorMode;

  return useCallback(() => {
    // Cancel ALL table apply modes via store APIs or events
    // Properties (store-based)
    const propStore = usePropertyStore.getState();
    propStore.setApplyByClickMode(false);
    propStore.setActivePropertyIdForClick(null);

    // Loadcase (local state - dispatch custom event)
    window.dispatchEvent(new CustomEvent("cancelLoadcaseApplyModes"));

    // Supports (local state - dispatch custom event)
    window.dispatchEvent(new CustomEvent("cancelSupportsApplyModes"));

    clearCursorMode();
  }, [clearCursorMode]);
};

/**
 * Hook to listen for global selection changes
 */
export const useGlobalSelectionListener = (
  onTableChange?: (table: TableType, rowId: string | null) => void,
  onCursorModeChange?: (mode: CursorMode) => void
) => {
  const { activeTable, activeRowId, activeCursorMode } =
    useGlobalSelectionStore();

  useEffect(() => {
    if (onTableChange) {
      onTableChange(activeTable, activeRowId);
    }
  }, [activeTable, activeRowId, onTableChange]);

  useEffect(() => {
    if (onCursorModeChange) {
      onCursorModeChange(activeCursorMode);
    }
  }, [activeCursorMode, onCursorModeChange]);
};
